import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Bug, Sparkles, ListChecks, Package, ShoppingBag, Building2, UserRound, RefreshCw, FileText, PhoneCall, Factory } from "lucide-react";

const SECTIONS = [
  {
    title: "Quotes",
    subtitle: "Quotes created by you and the team.",
    cards: [
      { key: "quotes_total",    label: "Total Quotes", caption: "Everyone (mine)",     Icon: FileText, iconClass: "bg-indigo-500/10 ring-indigo-500/20 text-indigo-300", myKey: "my_quotes_count" },
    ],
  },
  {
    title: "Total Items",
    subtitle: "Bug tracker & feature requests overall.",
    cards: [
      { key: "items_open",     label: "Open Items", caption: "Everyone (mine)", Icon: ListChecks, iconClass: "bg-pink-500/10 ring-pink-500/20 text-pink-300",       myKey: "my_items_open" },
      { key: "items_total",    label: "Total Items",caption: "Everyone (mine)", Icon: Sparkles,   iconClass: "bg-fuchsia-500/10 ring-fuchsia-500/20 text-fuchsia-300", myKey: "my_items_total" },
      { key: "items_bugs",     label: "Bugs",       caption: "Everyone (mine)", Icon: Bug,        iconClass: "bg-red-500/10 ring-red-500/20 text-red-300",         myKey: "my_items_bugs" },
      { key: "items_features", label: "Features",   caption: "Everyone (mine)", Icon: Sparkles,   iconClass: "bg-emerald-500/10 ring-emerald-500/20 text-emerald-300", myKey: "my_items_features" },
    ],
  },
  {
    title: "Database Statistics",
    subtitle: "Data synced from Talmia I",
    cards: [
      { key: "products_count",  label: "Products",  caption: "Total Products",    Icon: Package,     iconClass: "bg-indigo-500/10 ring-indigo-500/20 text-indigo-300" },
      { key: "orders_count",    label: "Orders",    caption: "Total orders",      Icon: ShoppingBag, iconClass: "bg-orange-500/10 ring-orange-500/20 text-orange-300" },
      { key: "persons_count",   label: "Persons",   caption: "Total Persons",     Icon: UserRound,   iconClass: "bg-pink-500/10 ring-pink-500/20 text-pink-300" },
      { key: "calls_count",     label: "Calls",     caption: "Total Calls",       Icon: PhoneCall,   iconClass: "bg-sky-500/10 ring-sky-500/20 text-sky-300" },
      { key: "suppliers_count", label: "Suppliers", caption: "Total Suppliers",   Icon: Factory,     iconClass: "bg-amber-500/10 ring-amber-500/20 text-amber-300" },
    ],
  },
];

export default function Home() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/home/dashboard");
      setStats(res.data || {});
    } catch (e) {
      // silent fail — cards will just show "—"
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      {/* Raj's famous swoosh — fixed backdrop behind everything on Home only */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/25 via-transparent to-orange-600/15" />
        <div
          className="absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(circle at 82% 8%, rgba(255,104,32,0.22) 0%, transparent 42%), radial-gradient(circle at 12% 92%, rgba(90,79,207,0.28) 0%, transparent 45%)",
          }}
        />
      </div>
      <div className="relative z-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight" data-testid="home-title">
            Welcome, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-slate-400 mt-3 text-base max-w-2xl">
            A quick snapshot of everything Talmia is tracking right now.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border border-white/10 text-slate-200 hover:bg-white/5 disabled:opacity-50"
          data-testid="home-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {SECTIONS.map(({ title, subtitle, cards }) => (
        <section key={title} className="mb-8" data-testid={`home-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="mb-3">
            <h2 className="font-display text-sm uppercase tracking-wider text-slate-400">{title}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {cards.map(({ key, label, caption, Icon, iconClass, myKey }) => {
              const value = stats[key];
              const myValue = myKey ? stats[myKey] : undefined;
              return (
                <div
                  key={key}
                  className="card-dark p-5 border border-white/5 flex items-start gap-4"
                  data-testid={`home-stat-${key}`}
                >
                  <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ring-1 shrink-0 ${iconClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
                    <div
                      className="font-display text-3xl font-bold text-white mt-1 leading-none"
                      data-testid={`home-stat-${key}-value`}
                    >
                      {loading && value === undefined ? "—" : Number(value ?? 0).toLocaleString()}
                      {myKey && (
                        <span className="text-slate-400 font-normal text-lg ml-2" data-testid={`home-stat-${key}-mine`}>
                          ({loading && myValue === undefined ? "—" : Number(myValue ?? 0).toLocaleString()})
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{caption}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
      </div>
    </Layout>
  );
}
